import React, { useContext, useEffect, useState } from "react";
import { ILoginInfo } from "../models/ILoginInfo";
import { Context } from "../utils/AppContext";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { getUserDetails, getUserToken, decrypt, isSuperAdmin } from "../utils/Util";
import { ensureFreshToken } from "../services/tokenRefresh";
import { checkHasSalonOrderRole } from "../services/checkRole";
import { getLinkedRestaurants, useActiveRestaurantId } from "../utils/activeRestaurant";

const AWS_DEPT_ID = "a1adf997-e85a-40da-823c-7477e0d2cf09";

const checkIsAwsUser = (): boolean => {
  const userJson = localStorage.getItem("user");
  if (!userJson) return false;
  try {
    const user = JSON.parse(decrypt(userJson));
    return Array.isArray(user.departmentIds) && user.departmentIds.includes(AWS_DEPT_ID);
  } catch {
    return false;
  }
};



const SUPER_LINKS = [
  { to: "/adminOrderManagement", label: "Sipariş Yönetimi",  icon: "bi-bag-check-fill" },
  { to: "/restaurantsPage",   label: "Restaurantlar",         icon: "bi-shop" },
  { to: "/addRestaurantPage", label: "Restaurant Ekle",       icon: "bi-plus-square-fill" },
  { to: "/addTownPage",       label: "Kent Ekle",             icon: "bi-geo-alt-fill" },
  { to: "/addFoodCategoryPage", label: "Kategori Ekle",       icon: "bi-tag-fill" },
  { to: "/addBillPage",       label: "Fatura Ekle",           icon: "bi-receipt" },
  { to: "/restaurantsOrders", label: "Restaurant Siparişleri",icon: "bi-bag-check-fill" },
  { to: "/destek",            label: "Destek",                icon: "bi-headset" },
  { to: "/liveChat",          label: "Canlı Destek",          icon: "bi-chat-dots-fill" },
  { to: "/liveChatManagement", label: "Canlı Destek Yönetimi", icon: "bi-diagram-3-fill" },
  { to: "/usersPage",         label: "Kullanıcı Yönetimi",   icon: "bi-people-fill" },
  { to: "/sliderPage",        label: "Slider",                icon: "bi-images" },
  { to: "/adminWorkingHours",    label: "Çalışma Saatleri",   icon: "bi-clock-fill" },
  { to: "/adminPaymentMethods", label: "Ödeme Yöntemleri",  icon: "bi-wallet-fill" },
  { to: "/adminPromoCodes",     label: "Promosyon Kodları",  icon: "bi-tag-fill" },
  { to: "/feedbackManagement", label: "Feedback Yönetimi",  icon: "bi-chat-square-heart-fill" },
  { to: "/adminReservationManagement", label: "Online Rezervasyon",  icon: "bi-calendar-check-fill" },
  { to: "/incidentManagement", label: "Olay Bildirimi",      icon: "bi-flag-fill" },
  { to: "/mailManagement",   label: "Mail Yönetimi",         icon: "bi-envelope-fill" },
  { to: "/dineIn",           label: "Salon Siparişi",        icon: "bi-shop-window" },
];

function Navbar(props: { user: ILoginInfo }) {
  const [isSuperUser, setIsSuperUser] = useState<boolean>(false);
  const [isAwsUser, setIsAwsUser] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hostingDropdownOpen, setHostingDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Menü açıkken arkadaki sayfa kaymasın; yoksa parmak hareketi menü yerine sayfayı kaydırır.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileMenuOpen]);
  // Hamburger menü React state'i ile yönetiliyor: Bootstrap JS hem CDN'den hem bundle'dan
  // yüklendiği için data-bs-toggle iki kez tetikleniyor ve menü kapanmıyordu.
  const [navOpen, setNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [userName, setUserName] = useState("");

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);


  React.useEffect(() => {
    setIsSuperUser(isSuperAdmin());
    setIsAwsUser(checkIsAwsUser());
    getUserDetails().then((userDetails) => {
      if (userDetails) {
        setUserName(`${userDetails.customerName} ${userDetails.customerSurName}`.trim());
      }
    });
  }, []);

  const context = useContext(Context);
  const Navigate = useNavigate();
  const location = useLocation();

  // Çok restoranlı hesapta bu sekmenin restoranı her sayfada görünsün (seçim tüm sayfalara uygulanır).
  const activeRestaurantId = useActiveRestaurantId();
  const linkedRestaurants = getLinkedRestaurants();
  const activeRestaurantName = linkedRestaurants.length > 1
    ? linkedRestaurants.find((r) => r.id.toLowerCase() === activeRestaurantId.toLowerCase())?.name ?? ""
    : "";

  // Salon rolü olan herkes (SuperAdmin dahil) bağlantıyı Dashboard'un yanında görür.
  // Sayfa değiştikçe yeniden sorulur (kısa önbellekli) ki yeni verilen rol hemen yansısın.
  const [hasSalonRole, setHasSalonRole] = useState(false);
  React.useEffect(() => {
    let cancelled = false;
    checkHasSalonOrderRole(getUserToken()?.token).then((ok) => { if (!cancelled) setHasSalonRole(ok); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  const fncLogout = () => {
    localStorage.removeItem("user");
    Navigate("/", { replace: true });
  };

  // Auth guard: login sayfası hariç token yoksa veya süresi geçmişse logout.
  // Süresi geçmiş ama refreshToken hâlâ geçerliyse önce sessizce yenilemeyi dener.
  React.useEffect(() => {
    if (location.pathname === "/") return;
    let cancelled = false;
    ensureFreshToken().then((ok) => {
      if (cancelled || ok) return;
      localStorage.removeItem("user");
      Navigate("/", { replace: true });
    });
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Sayfa değişince mobil menüler kapansın
  React.useEffect(() => {
    setNavOpen(false);
    setHostingDropdownOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="navbar navbar-expand-lg bg-body-tertiary">
        <div className="container-fluid">
          <a className="navbar-brand" href="/" onClick={e => { e.preventDefault(); Navigate("/dashboard"); }}>
            Mutfacık Admin
          </a>
          {activeRestaurantName && (
            <button type="button" className="nav-active-restaurant" onClick={() => Navigate("/dashboard")}
              title="Bu sekmede bakılan restoran — değiştirmek için Dashboard'daki listeyi kullanın">
              <i className="bi bi-shop"></i>
              <span>{activeRestaurantName}</span>
            </button>
          )}
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setNavOpen((o) => !o)}
            aria-controls="navbarSupportedContent"
            aria-expanded={navOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className={`collapse navbar-collapse${navOpen ? " show" : ""}`} id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <NavLink className="nav-link" to="/dashboard">
                  Dashboard
                </NavLink>
              </li>
              {hasSalonRole && (
                <li className="nav-item">
                  <NavLink className="nav-link" to="/salon">
                    Salon Siparişi
                  </NavLink>
                </li>
              )}
              <li className="nav-item">
                <NavLink className={"nav-link"} to="/allOrders">
                  Geçmiş Siparişler
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={"nav-link"} to="/restaurantSettings">
                  Restoran Ayarları
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={"nav-link"} to="/paymentMethods">
                  Ödeme Yöntemleri
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={"nav-link"} to="/workingHours">
                  Çalışma Saatleri
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={"nav-link"} to="/promoCodes">
                  Promosyon Kodları
                </NavLink>
              </li>

{isSuperUser ? (
  isMobile ? (
    /* ── MOBILE: floating menu button ── */
    <li className="nav-item">
      <a className="nav-link" href="#" role="button"
        onClick={(e) => { e.preventDefault(); setNavOpen(false); setMobileMenuOpen(true); }}>
        <i className="bi bi-grid-3x3-gap-fill me-1"></i>Menü
      </a>
    </li>
  ) : (
    /* ── DESKTOP: dropdown ── */
    <li className="nav-item dropdown" style={{position: 'relative'}}>
      <a className="nav-link dropdown-toggle" href="#" role="button"
        onClick={(e) => { e.preventDefault(); setDropdownOpen(o => !o); }}>
        SuperAdmin
      </a>
      {dropdownOpen && (
        <>
          <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 999}}
            onClick={() => setDropdownOpen(false)} />
          <ul className="dropdown-menu show" style={{zIndex: 1000}}>
            {SUPER_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink className="dropdown-item" to={l.to} onClick={() => setDropdownOpen(false)}>
                  <i className={`bi ${l.icon} me-2`}></i>{l.label}
                </NavLink>
              </li>
            ))}
            <li><hr className="dropdown-divider" /></li>
            <li>
              <a className="dropdown-item" role="button" onClick={() => { setDropdownOpen(false); fncLogout(); }}>
                <i className="bi bi-box-arrow-right me-2"></i>Logout
              </a>
            </li>
          </ul>
        </>
      )}
    </li>
  )
) : (
  <li className="nav-item">
    <a className="nav-link" onClick={fncLogout} role="button">Logout</a>
  </li>
)}

{isAwsUser && (
  <li className="nav-item dropdown" style={{position: 'relative'}}>
    <a className="nav-link dropdown-toggle" href="#" role="button"
      onClick={(e) => { e.preventDefault(); setHostingDropdownOpen(o => !o); }}>
      <i className="bi bi-hdd-network-fill me-1"></i>Hosting
    </a>
    {hostingDropdownOpen && (
      <>
        {/* Dışarı tıklayınca kapatan katman sadece masaüstünde: mobilde menü akış içinde
            (position: static) durduğu için z-index'i işlemiyor, katman linklerin üstüne biniyordu. */}
        {!isMobile && (
          <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 999}}
            onClick={() => setHostingDropdownOpen(false)} />
        )}
        <ul className="dropdown-menu show" style={{zIndex: 1000}}>
          <li>
            <NavLink className="dropdown-item" to="/aws" onClick={() => setHostingDropdownOpen(false)}>
              <i className="bi bi-cloud-fill me-2"></i>AWS
            </NavLink>
          </li>
          <li>
            <NavLink className="dropdown-item" to="/hostinger" onClick={() => setHostingDropdownOpen(false)}>
              <i className="bi bi-hdd-rack-fill me-2"></i>Hostinger
            </NavLink>
          </li>
        </ul>
      </>
    )}
  </li>
)}

         
              {/* <li className="nav-item">
                <a className="nav-link disabled" aria-disabled="true">
                  {props.user.firstName + " " + props.user.lastName} (
                  {selector.length}) - {context.name}{" "}
                </a>
              </li> */}
            </ul>
            {userName && (
              <NavLink to="/profil" title="Profilim" style={({ isActive }) => ({
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: isActive ? "#eef2ff" : "#f8faff", border: `1.5px solid ${isActive ? "#a5b4fc" : "#e0e7ff"}`,
                borderRadius: "20px", padding: "5px 14px 5px 6px",
                textDecoration: "none", cursor: "pointer", marginBottom: isMobile ? 8 : 0,
              })}>
                <div style={{
                  width: "28px", height: "28px", borderRadius: "50%",
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  color: "#fff", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: "0.68rem", fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {userName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                </div>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>
                  {userName}
                </span>
                <i className="bi bi-chevron-right" style={{ fontSize: "0.7rem", color: "#9ca3af" }}></i>
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* ── MOBILE BOTTOM SHEET ── */}
      {isMobile && mobileMenuOpen && (
        <>
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
          `}</style>
          {/* Backdrop */}
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 1040,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(3px)",
            }}
          />
          {/* Sheet — ekran yüksekliğiyle sınırlı; başlık ve çıkış sabit, ızgara kayar */}
          <div className="mobile-sheet" style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1050,
            background: "#fff",
            borderRadius: "24px 24px 0 0",
            padding: "0 0 env(safe-area-inset-bottom)",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
            animation: "slideUp 0.35s cubic-bezier(0.32, 0.72, 0, 1)",
          }}>
            {/* Handle */}
            <div className="mobile-sheet__fixed" style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
              <div style={{ width: "40px", height: "4px", borderRadius: "4px", background: "#e5e7eb" }} />
            </div>
            {/* Header */}
            <div className="mobile-sheet__fixed" style={{
              padding: "8px 20px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderBottom: "1px solid #f1f5f9",
            }}>
              <span style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
                <i className="bi bi-shield-fill-check me-2" style={{ color: "#6366f1" }}></i>SuperAdmin
              </span>
              <button onClick={() => setMobileMenuOpen(false)}
                style={{ background: "none", border: "none", fontSize: "1.2rem", color: "#9ca3af", cursor: "pointer", padding: "4px" }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            {/* Grid of links — tek kayan bölge */}
            <div className="mobile-sheet__scroll" style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px", padding: "16px", alignContent: "start",
            }}>
              {SUPER_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileMenuOpen(false)}
                  style={({ isActive }) => ({
                    display: "flex", flexDirection: "column", alignItems: "center",
                    gap: "6px", padding: "14px 8px", borderRadius: "14px",
                    background: isActive ? "#eef2ff" : "#f8faff",
                    border: `1.5px solid ${isActive ? "#c7d2fe" : "#f1f5f9"}`,
                    textDecoration: "none", color: isActive ? "#4f46e5" : "#374151",
                    fontWeight: 600, fontSize: "0.72rem", textAlign: "center",
                    transition: "all 0.15s",
                  })}
                >
                  <i className={`bi ${l.icon}`} style={{ fontSize: "1.5rem" }}></i>
                  {l.label}
                </NavLink>
              ))}
            </div>
            {/* Logout */}
            <div className="mobile-sheet__fixed mobile-sheet__footer" style={{ padding: "12px 16px 20px" }}>
              <button
                onClick={() => { setMobileMenuOpen(false); fncLogout(); }}
                style={{
                  width: "100%", padding: "13px", borderRadius: "14px", border: "none",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#fff", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  boxShadow: "0 4px 14px rgba(220,38,38,0.3)",
                }}
              >
                <i className="bi bi-box-arrow-right"></i> Çıkış Yap
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Navbar;
